/**
 * MailtoAttachmentModal
 *
 * Shown when the user has picked "My mail client" (mailto) for a flow
 * (invoice send/remind, report share). Many clients (Outlook web, iOS Mail,
 * Yahoo) silently drop or mangle long `mailto:` bodies — so we show a
 * tidy modal with:
 *   - Download PDF (ready-to-attach)
 *   - To / Subject (copyable)
 *   - Pre-formatted body with "Copy email body" button
 *   - "Open in mail app" button that fires the mailto: URL
 *
 * The modal is self-dismissing: once the user clicks "Open in mail app"
 * we close it. "Just copy & paste" workflow also closes via the X button.
 */
import { useEffect, useRef, useState } from "react";
import { Copy, Download, Mail, X, CheckCircle2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { downloadFile } from "@/lib/api";

export default function MailtoAttachmentModal({
  open,
  onClose,
  to = "",
  subject = "",
  body = "",
  mailtoUrl = "",
  pdfDownloadUrl = "",      // relative API path, e.g. "/invoices/xyz/export"
  pdfFilename = "",         // e.g. "Invoice-INV-00042.pdf"
  title = "Compose email",
  subtitle = "",
}) {
  const [downloaded, setDownloaded] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open) setDownloaded(false);
  }, [open]);

  if (!open) return null;

  const downloadPdf = async () => {
    if (!pdfDownloadUrl) return;
    try {
      await downloadFile(pdfDownloadUrl, pdfFilename);
      setDownloaded(true);
      toast.success(`${pdfFilename} downloaded — attach it in your mail draft`);
    } catch {
      toast.error("PDF download failed");
    }
  };

  const copyBody = async () => {
    try {
      await navigator.clipboard?.writeText(body || "");
      toast.success("Email body copied — paste into your draft");
    } catch {
      // Fallback: select the textarea
      if (bodyRef.current) {
        bodyRef.current.select();
        try {
          document.execCommand("copy");
          toast.success("Email body copied");
        } catch { toast.error("Copy failed — select text manually"); }
      } else { toast.error("Copy failed"); }
    }
  };

  const copyField = async (value, label) => {
    try {
      await navigator.clipboard?.writeText(value || "");
      toast.success(`${label} copied`);
    } catch { toast.error("Copy failed"); }
  };

  const openMailClient = () => {
    if (!mailtoUrl) {
      toast.error("No mailto URL available");
      return;
    }
    // Give the browser a tick to render the download before firing mailto
    // (some clients steal focus and prevent concurrent download prompts).
    window.location.href = mailtoUrl;
    toast.info("Opened your native mail client — attach the PDF before sending");
    setTimeout(() => onClose?.(), 250);
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 grid place-items-center p-4"
      onClick={onClose}
      data-testid="mailto-attachment-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border w-full max-w-xl max-h-[92vh] overflow-y-auto shadow-xl"
        style={{ borderColor: "var(--ll-ink)" }}
      >
        <div
          className="px-5 py-3 border-b bg-[#0b5d9e] text-white flex items-start justify-between"
          style={{ borderColor: "var(--ll-border)" }}
        >
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest opacity-75">Compose in your mail client</div>
            <div className="font-display font-bold text-sm flex items-center gap-2">
              <Mail size={14} /> {title}
            </div>
            {subtitle && (
              <div className="text-[11px] opacity-80 mt-0.5 truncate">{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="mailto-modal-close"
            className="text-white/80 hover:text-white"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {/* PDF attachment card */}
          {pdfDownloadUrl && (
            <div
              className="border p-3 flex items-center gap-3"
              style={{
                background: downloaded ? "#ECFDF5" : "#FFFBEB",
                borderColor: downloaded ? "#10B981" : "#F59E0B",
              }}
              data-testid="mailto-pdf-card"
            >
              <Paperclip
                size={22}
                className="shrink-0"
                style={{ color: downloaded ? "#047857" : "#B45309" }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium">
                  {downloaded ? "PDF downloaded" : "Attach this PDF"}
                </div>
                <div className="text-xs text-[var(--ll-ink-soft)] truncate">
                  {pdfFilename}
                </div>
              </div>
              <button
                type="button"
                onClick={downloadPdf}
                data-testid="mailto-download-pdf"
                className="text-xs px-3 py-2 text-white hover:opacity-90 flex items-center gap-1.5 shrink-0"
                style={{ background: downloaded ? "#10B981" : "#B45309" }}
              >
                {downloaded ? (
                  <><CheckCircle2 size={12} /> Downloaded</>
                ) : (
                  <><Download size={12} /> Download</>
                )}
              </button>
            </div>
          )}

          {/* To + Subject */}
          <div className="space-y-2">
            <FieldRow
              label="To"
              value={to}
              testid="mailto-to"
              onCopy={() => copyField(to, "Recipient")}
            />
            <FieldRow
              label="Subject"
              value={subject}
              testid="mailto-subject"
              onCopy={() => copyField(subject, "Subject")}
            />
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
                Email body
              </label>
              <button
                type="button"
                onClick={copyBody}
                data-testid="mailto-copy-body"
                className="text-[11px] uppercase tracking-widest flex items-center gap-1 px-2 py-1 bg-[#0b5d9e] text-white hover:opacity-90"
              >
                <Copy size={11} /> Copy email body
              </button>
            </div>
            <textarea
              ref={bodyRef}
              value={body}
              readOnly
              rows={8}
              data-testid="mailto-body"
              className="w-full px-3 py-2 border text-sm font-mono-fin bg-[var(--ll-bg-card)]"
              style={{ borderColor: "var(--ll-border)" }}
            />
          </div>

          <div className="text-[11px] text-[var(--ll-ink-muted)] leading-relaxed">
            Some mail clients (Outlook web, iOS Mail, Yahoo) trim or re-wrap long
            <code> mailto: </code> drafts. If your draft looks mangled when it opens,
            use the <strong>Copy email body</strong> button above and paste the clean text in.
          </div>
        </div>

        <div
          className="sticky bottom-0 bg-white border-t px-5 py-3 flex items-center justify-end gap-2"
          style={{ borderColor: "var(--ll-border)" }}
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="mailto-cancel"
            className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)]"
            style={{ borderColor: "var(--ll-border)" }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={openMailClient}
            disabled={!mailtoUrl}
            data-testid="mailto-open"
            className="text-sm px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Mail size={13} /> Open in mail app
          </button>
        </div>
      </div>
    </div>
  );
}


function FieldRow({ label, value, onCopy, testid }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] w-16 shrink-0">
        {label}
      </div>
      <div
        className="flex-1 min-w-0 border px-2 py-1.5 bg-[var(--ll-bg-card)] text-xs truncate font-mono-fin"
        style={{ borderColor: "var(--ll-border)" }}
        data-testid={`${testid}-value`}
        title={value}
      >
        {value || "—"}
      </div>
      <button
        type="button"
        onClick={onCopy}
        data-testid={`${testid}-copy`}
        className="p-1.5 border hover:bg-[var(--ll-mint)]"
        style={{ borderColor: "var(--ll-border)" }}
        title={`Copy ${label}`}
      >
        <Copy size={11} />
      </button>
    </div>
  );
}
